import type { WindowLocation } from '@reach/router';
import { useLocation } from '@reach/router';
import type React from 'react';
import { useIntl } from 'react-intl';

import { TextButton } from '@xing-com/button';
import { IconArrowLeft, IconArrowRight } from '@xing-com/icons';

import * as Styled from './new-pagination.styled';

const isFirstPage = (targetPage?: number) => !targetPage || targetPage === 1;
const getUrl = (location: WindowLocation, pageNumber?: number) =>
  isFirstPage(pageNumber)
    ? location.pathname
    : `${location.pathname}?page=${pageNumber}`;

type PageProps = {
  page: number;
  currentPage: number;
};
const Page: React.FC<PageProps> = ({ page, currentPage }) => {
  const isActive = page === currentPage;
  const location = useLocation();
  const url = getUrl(location, page);

  return (
    <li key={page} aria-label={page.toString()}>
      <TextButton
        disabled={isActive}
        size="large"
        to={isActive ? undefined : url}
      >
        {page}
      </TextButton>
    </li>
  );
};

const Ellipsis: React.FC = () => (
  <Styled.PageItem className="disabledPageItem">
    <span>...</span>
  </Styled.PageItem>
);

/*
 * Output format: [1, ..., c-1, c, c+1, ..., t]
 */
type PagesProps = {
  currentPage: number;
  totalPages: number;
};
const Pages: React.FC<PagesProps> = ({ currentPage, totalPages }) => {
  // How many elements at each side of the currentPage
  const windowDelta = 1;
  const windowSize = 1 + windowDelta * 2;
  // The positions that maintain the number of elements displayed constant
  const rightCroppingLimit = 1 + windowSize;
  const leftCroppingLimit = totalPages - windowSize;
  // The first and last element to be rendered in the window
  const right = Math.max(currentPage, rightCroppingLimit) + windowDelta;
  const left = Math.min(currentPage, leftCroppingLimit) - windowDelta;

  // Calculate the pages that should be included by definition of the limits
  const range = [];
  range.push(1);
  if (totalPages > 1) {
    for (let i = left; i <= right; i++) {
      if (i > 1 && i < totalPages) {
        range.push(i);
      }
    }
    range.push(totalPages);
  }

  // Fill the sequence gaps with ellipses, or a page if there's only one missing
  const result = [];
  for (let i = 0; i < range.length; i++) {
    const pageValue = range[i];
    if (i > 0) {
      const previousValue = range[i - 1];
      if (pageValue - previousValue === 2) {
        result.push(
          <Page
            page={previousValue + 1}
            currentPage={currentPage}
            key={previousValue + 1}
          />
        );
      } else if (pageValue - previousValue > 2) {
        result.push(<Ellipsis key={previousValue + 1} />);
      }
    }
    result.push(
      <Page page={pageValue} key={pageValue} currentPage={currentPage} />
    );
  }

  return <>{result}</>;
};

type ChevronProps = {
  direction?: 'left' | 'right';
  targetPage?: number;
  disabled?: boolean;
};
const Chevron: React.FC<ChevronProps> = ({
  direction,
  targetPage,
  disabled = false,
  ...props
}) => {
  const intl = useIntl();
  const arrowIcon = direction === 'left' ? IconArrowLeft : IconArrowRight;
  const relDirection = direction === 'left' ? 'prev' : 'next';
  const location = useLocation();
  const url = getUrl(location, targetPage);
  const labelKey =
    direction === 'left' ? 'CP_PAGINATION_LEFT' : 'CP_PAGINATION_RIGHT';
  const label = intl.formatMessage({ id: labelKey });
  return (
    <li aria-label={label}>
      <TextButton
        icon={arrowIcon}
        size="large"
        to={!disabled ? url : undefined}
        disabled={disabled}
        rel={relDirection}
        {...props}
      />
    </li>
  );
};

type PaginationProps = {
  currentPage: number;
  total: number;
  perPage: number;
  limitPages?: number;
};
export const NewPagination: React.FC<PaginationProps> = ({
  currentPage,
  total,
  perPage,
  limitPages,
}) => {
  const { formatMessage } = useIntl();
  let totalPages = Math.ceil(total / perPage) || 1;
  if (limitPages !== undefined && totalPages > limitPages) {
    totalPages = limitPages;
  }
  const hasOnlyOnePage = totalPages === 1;

  return (
    <Styled.Wrapper>
      {!hasOnlyOnePage && (
        <Styled.Pagination>
          <Chevron
            aria-label={formatMessage({
              id: 'CP_PAGINATION_LEFT',
            })}
            direction="left"
            targetPage={currentPage - 1}
            disabled={currentPage === 1}
          />
          <Pages currentPage={currentPage} totalPages={totalPages} />
          <Chevron
            aria-label={formatMessage({
              id: 'CP_PAGINATION_RIGHT',
            })}
            direction="right"
            targetPage={currentPage + 1}
            disabled={currentPage === totalPages}
          />
        </Styled.Pagination>
      )}
    </Styled.Wrapper>
  );
};
